import { z } from 'zod';

export const EQUIPMENT_UNIT_PREFIX = 'equipment_unit_';
export const EQUIPMENT_SERVICE_PREFIX = 'equipment_service_';
export const EQUIPMENT_BILL_DISCOUNT_TYPE = 'equipment_bill_discount';
/**
 * Shown wherever a bill discount carries no text of its own — legacy rows
 * saved before discounts were itemized, and new rows left unlabelled.
 */
export const BILL_DISCOUNT_FALLBACK_LABEL = 'Bill discount';
/**
 * Per-item discount — one row per discounted unit, service visit or other
 * charge. Deliberately outside the unit/service prefixes so no recognizer
 * mistakes it for a chargeable item.
 */
export const EQUIPMENT_ITEM_DISCOUNT_TYPE = 'equipment_item_discount';

export interface UnitNotePayload {
  assetTag: string;
  /**
   * Self-describing name for units that have no catalog entry to look up —
   * see `CUSTOM_UNIT_KEY_PREFIX`. Null/absent on catalog units, whose label
   * comes from the equipment catalog (and may be renamed there afterwards).
   */
  label?: string | null;
  location: string | null;
  startDate: string | null;
  endDate: string | null;
  /**
   * Explicit rental-days estimate. When non-null the unit is in "days mode":
   * billing uses this value (floored at minDays) and start/end dates are ignored.
   * Null/undefined means the legacy date-derived behavior.
   */
  days?: number | null;
  /**
   * Flat dollar discount for this unit. The unit's own amount stays gross —
   * the discount is emitted as its own negative sub-item so job cards and
   * invoices show it as a separate line.
   */
  discount?: number | null;
  setupFee: number;
  dailyRate: number;
  minDays: number;
}

export interface VisitNotePayload {
  rateKey: string;
  label: string;
  dates: string[];
  qty: number;
  rate: number;
  /** Flat dollar discount for this visit. See `UnitNotePayload.discount`. */
  discount?: number | null;
}

export interface UnitInput extends UnitNotePayload {
  typeKey: string;
  sortOrder: number;
}

export interface VisitInput extends VisitNotePayload {
  sortOrder: number;
}

// Runtime validation schemas (inline — not exported to keep surface minimal)
const unitNoteSchema = z.object({
  assetTag: z.string(),
  // Absent on catalog units (and on every note written before custom units
  // existed) → normalized to null on parse.
  label: z.string().nullable().default(null),
  location: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  // Optional on input (legacy notes omit it) → normalized to null on parse.
  days: z.number().nullable().default(null),
  discount: z.number().nullable().default(null),
  setupFee: z.number(),
  dailyRate: z.number(),
  minDays: z.number(),
});

const visitNoteSchema = z.object({
  rateKey: z.string(),
  label: z.string(),
  dates: z.array(z.string()),
  qty: z.number(),
  rate: z.number(),
  discount: z.number().nullable().default(null),
});

export function unitTypeFor(typeKey: string): string {
  return EQUIPMENT_UNIT_PREFIX + typeKey;
}

export function visitTypeFor(rateKey: string): string {
  return EQUIPMENT_SERVICE_PREFIX + rateKey;
}

export function isUnitType(t: string): boolean {
  return t.startsWith(EQUIPMENT_UNIT_PREFIX);
}

export function isItemDiscountType(t: string): boolean {
  return t === EQUIPMENT_ITEM_DISCOUNT_TYPE;
}

export function isVisitType(t: string): boolean {
  return t.startsWith(EQUIPMENT_SERVICE_PREFIX);
}

/**
 * Marks a unit type key as belonging to a one-off piece of equipment typed in
 * on the job rather than defined in the settings catalog. The ':' can never
 * appear in a catalog key (those are slugs), so the two namespaces are
 * disjoint. Each custom unit gets its own key, so two of them never merge into
 * one group even when they share a name.
 */
export const CUSTOM_UNIT_KEY_PREFIX = 'custom:';

export function makeCustomUnitTypeKey(id: string): string {
  return CUSTOM_UNIT_KEY_PREFIX + id;
}

export function isCustomUnitTypeKey(typeKey: string): boolean {
  return typeKey.startsWith(CUSTOM_UNIT_KEY_PREFIX);
}

export function unitTypeKeyOf(t: string): string {
  return t.slice(EQUIPMENT_UNIT_PREFIX.length);
}

export function visitRateKeyOf(t: string): string {
  return t.slice(EQUIPMENT_SERVICE_PREFIX.length);
}

/**
 * Inputs MUST be `YYYY-MM-DD` ISO date strings (not datetimes).
 */
export function computeUnitDays(p: {
  startDate: string | null;
  endDate: string | null;
  minDays: number;
  days?: number | null;
}): number {
  const floor = Math.max(0, p.minDays);
  // Days mode — explicit estimate wins over any dates, floored at minDays.
  if (p.days != null) return Math.max(floor, Math.max(0, p.days));
  if (!p.startDate || !p.endDate) return floor;
  // Inverted range — treat as no valid period, fall back to minDays
  if (p.endDate < p.startDate) return floor;
  const ms = new Date(p.endDate).getTime() - new Date(p.startDate).getTime();
  const inclusive = ms >= 0 ? Math.max(1, Math.floor(ms / 86_400_000) + 1) : 1;
  return Math.max(p.minDays, inclusive);
}

export function computeUnitTotal(p: UnitNotePayload): number {
  const hasDates = !!(p.startDate && p.endDate);
  // No estimate and no dates → setup fee only (legacy behavior).
  if (p.days == null && !hasDates) {
    return p.setupFee;
  }
  const days = computeUnitDays(p);
  return p.setupFee + days * p.dailyRate;
}

/** Gross rental total less this unit's discount, floored at zero. */
export function computeUnitNet(p: UnitNotePayload): number {
  const discount = Math.max(0, p.discount ?? 0);
  return Math.max(0, computeUnitTotal(p) - discount);
}

export function buildUnitSubItem(input: UnitInput) {
  const note: UnitNotePayload = {
    assetTag: input.assetTag,
    // Catalog units keep the pre-custom note shape — no label key at all.
    ...(input.label ? { label: input.label } : {}),
    location: input.location,
    startDate: input.startDate,
    endDate: input.endDate,
    days: input.days ?? null,
    discount: input.discount ?? null,
    setupFee: input.setupFee,
    dailyRate: input.dailyRate,
    minDays: input.minDays,
  };
  return {
    type: unitTypeFor(input.typeKey),
    amount: computeUnitTotal(note),
    note: JSON.stringify(note),
    sortOrder: input.sortOrder,
  };
}

export interface ItemDiscountInput {
  /**
   * Descriptor parts naming the discounted item, e.g. ["Dehumidifier", "D1"]
   * or ["Emergency service call"]. Empty parts are dropped.
   */
  labelParts: Array<string | null | undefined>;
  discount: number;
  sortOrder: number;
}

/**
 * The customer-facing discount row. Derived from the owning item's note on
 * every save, so it can never drift from the item it belongs to.
 */
export function buildItemDiscountSubItem(input: ItemDiscountInput) {
  const note = input.labelParts.filter((p): p is string => !!p && p.length > 0).join(' · ');
  return {
    type: EQUIPMENT_ITEM_DISCOUNT_TYPE,
    amount: -Math.max(0, input.discount),
    note,
    sortOrder: input.sortOrder,
  };
}

export function parseUnitSubItem(s: { type: string; amount: number; note: string | null | undefined }) {
  if (!isUnitType(s.type)) return null;
  if (!s.note) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(s.note);
  } catch {
    return null;
  }
  const parsed = unitNoteSchema.safeParse(raw);
  if (!parsed.success) return null;
  const typeKey = unitTypeKeyOf(s.type);
  return { typeKey, ...parsed.data, amount: s.amount };
}

/**
 * The display label for any sub-item. Custom units are the one type whose name
 * lives in the row itself — `typeMeta` is built from the catalog and has no
 * entry for them — so their note wins. Everything else resolves as usual.
 */
export function resolveUnitSubItemLabel(
  sub: { type: string; note: string | null | undefined },
  typeMeta: Record<string, { label: string } | undefined>,
): string {
  if (isUnitType(sub.type) && isCustomUnitTypeKey(unitTypeKeyOf(sub.type))) {
    const parsed = parseUnitSubItem({ type: sub.type, amount: 0, note: sub.note });
    if (parsed?.label) return parsed.label;
  }
  return typeMeta[sub.type]?.label ?? sub.type;
}

/** Gross visit total (qty × rate) less its discount, floored at zero. */
export function computeVisitNet(p: Pick<VisitNotePayload, 'qty' | 'rate' | 'discount'>): number {
  return Math.max(0, p.qty * p.rate - Math.max(0, p.discount ?? 0));
}

export function buildVisitSubItem(input: VisitInput) {
  const note: VisitNotePayload = {
    rateKey: input.rateKey,
    label: input.label,
    dates: input.dates,
    qty: input.qty,
    rate: input.rate,
    discount: input.discount ?? null,
  };
  return {
    type: visitTypeFor(input.rateKey),
    amount: input.qty * input.rate,
    note: JSON.stringify(note),
    sortOrder: input.sortOrder,
  };
}

export function parseVisitSubItem(s: { type: string; amount: number; note: string | null | undefined }) {
  if (!isVisitType(s.type)) return null;
  if (!s.note) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(s.note);
  } catch {
    return null;
  }
  const parsed = visitNoteSchema.safeParse(raw);
  if (!parsed.success) return null;
  const rateKey = visitRateKeyOf(s.type);
  return { ...parsed.data, rateKey, amount: s.amount };
}

export const EQUIPMENT_FLAT_TYPE = 'equipmentRental';

export function isFlatType(t: string): boolean {
  return t === EQUIPMENT_FLAT_TYPE;
}

export interface FlatInput {
  description: string;
  amount: number;
  discount?: number | null;
  sortOrder: number;
}

// A flat charge's note is the description as plain text. A discount has
// nowhere to live in that encoding, so a discounted flat switches to a JSON
// note. Undiscounted flats keep the plain-text form, and both parse back.
const flatNoteSchema = z.object({
  description: z.string(),
  discount: z.number().nullable().default(null),
});

export function buildFlatSubItem(input: FlatInput) {
  const discount = input.discount ?? null;
  return {
    type: EQUIPMENT_FLAT_TYPE,
    amount: input.amount,
    note: discount != null && discount > 0
      ? JSON.stringify({ description: input.description, discount })
      : input.description,
    sortOrder: input.sortOrder,
  };
}

export function parseFlatSubItem(s: {
  type: string;
  amount: number;
  note: string | null | undefined;
}): { description: string; discount: number | null; amount: number } | null {
  if (!isFlatType(s.type)) return null;
  const note = s.note ?? '';
  // Only an object carrying `description` is structured — a description that
  // happens to be valid JSON (e.g. "1000") stays a plain description.
  if (note.startsWith('{')) {
    try {
      const parsed = flatNoteSchema.safeParse(JSON.parse(note));
      if (parsed.success) {
        return { ...parsed.data, amount: s.amount };
      }
    } catch {
      // Fall through to plain-text handling.
    }
  }
  return { description: note, discount: null, amount: s.amount };
}

/** Gross flat amount less its discount, floored at zero. */
export function computeFlatNet(p: { amount: number; discount?: number | null }): number {
  return Math.max(0, p.amount - Math.max(0, p.discount ?? 0));
}

export interface BillDiscountInput {
  /** Free text naming what is being discounted. Empty renders as the fallback. */
  label: string;
  discount: number;
  sortOrder: number;
}

/**
 * A bill-level discount. Many may coexist on one equipment bill — each is its
 * own sub-item, with the label in the note and the money in the amount.
 */
export function buildBillDiscountSubItem(input: BillDiscountInput) {
  return {
    type: EQUIPMENT_BILL_DISCOUNT_TYPE,
    amount: -Math.max(0, input.discount),
    note: input.label,
    sortOrder: input.sortOrder,
  };
}

export function parseBillDiscountSubItem(s: {
  type: string;
  amount: number;
  note: string | null | undefined;
}): { label: string; discount: number } | null {
  if (s.type !== EQUIPMENT_BILL_DISCOUNT_TYPE) return null;
  return { label: s.note ?? '', discount: Math.abs(Number(s.amount) || 0) };
}
